import config from './config'
import { callAuthenticatedApi } from './authenticated-api'

const apiUrls = {
  remote: config.studioRemoteApiUrl,
  local: config.studioLocalApiUrl
}

const apiError = (err, fallbackMessage) => err.response?.data ?? { ok: false, error: { message: fallbackMessage } }

const getRealtimeConfig = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      method: 'get',
      url: `${apiUrls[apiSource]}/realtime/config`
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting realtime config")
  }
}

const saveUserPreferences = async ({ apiSource, apiKey, user }) => {
  try {
    let options = {
      method: 'patch',
      url: `${apiUrls[apiSource]}/saveUserPreferences/` + user.user_id,
      data: { preferences: user.preferences }
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error saving user prefs")
  }
}

const onboarding = async ({ apiSource, apiKey, user, client }) => {
  try {
    let options = {
      method: 'POST',
      url: `${apiUrls[apiSource]}/client/onboarding`,
      data: { user, client }
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error onboarding")
  }
}

const getApiKeys = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getApiKeys`,
      method: "get"
    }

    return await callApi({ apiKey, options, includeChildClientId:true })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting API Key list")
  }
}

const addChildClient = async ({ apiSource, apiKey, childClient }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/addChildClient`,
      method: "put",
      data: childClient
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error adding child client")
  }
}

const getCertificateManagementRows = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getCertificateManagementRows`,
      method: "get"
    }

    return await callApi({ apiKey, options, includeChildClientId:false })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting certificate management rows")
  }
}

const getClientCertificates = async ({ apiSource, apiKey, clientId }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getClientCertificates`,
      method: "get",
      params: clientId != null ? { client_id: clientId } : undefined
    }

    return await callApi({ apiKey, options, includeChildClientId:false })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting certificate details")
  }
}

const setClientCertificateType = async ({ apiSource, apiKey, clientId, provider, certificateType, appleEmail }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/setClientCertificateType`,
      method: "patch",
      data: {
        client_id: clientId,
        provider,
        certificate_type: certificateType,
        apple_email: appleEmail
      }
    }

    return await callApi({ apiKey, options, includeChildClientId:false })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error saving saving certificate type")
  }
}

const requestPrivateCertificate = async ({ apiSource, apiKey, clientId, provider }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/requestPrivateCertificate`,
      method: "post",
      data: {
        client_id: clientId,
        provider
      }
    }

    return await callApi({ apiKey, options, includeChildClientId:false })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error requesting private certificate")
  }
}

const downloadClientCsr = async ({ apiSource, apiKey, clientId }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/downloadClientCsr`,
      method: "get",
      params: clientId != null ? { client_id: clientId } : undefined
    }

    return await callApi({ apiKey, options, includeChildClientId:false })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error downloading CSR")
  }
}

const uploadOwnAppleCertificate = async ({ apiSource, apiKey, clientId, pem }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/uploadOwnAppleCertificate`,
      method: "patch",
      data: { client_id: clientId, pem }
    }

    return await callApi({ apiKey, options, includeChildClientId:false })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error uploading Apple certificate")
  }
}

const uploadOwnGoogleCredentials = async ({ apiSource, apiKey, clientId, credentials }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/uploadOwnGoogleCredentials`,
      method: "patch",
      data: { client_id: clientId, credentials }
    }

    return await callApi({ apiKey, options, includeChildClientId:false })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error uploading Google key")
  }
}


const deleteAccount = async ({ apiSource, apiKey, deletionDate }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/client/deleteAccount/${deletionDate}`,
      method: "delete",
      data: {}
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error deleting account")
  }
}

const recoverAccount = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/client/recoverAccount`,
      method: "delete",
      data: {}
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error recovering account")
  }
}


const getPlans = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/getPlans`,
      method: "get",
      data: {}
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting payment plans")
  }
}

const getUsage = async ({ apiSource, apiKey, history, billingPeriod }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/getUsage`,
      method: "get",
      data: {}
    }

    if (history) options.data.history = true
    if (billingPeriod) options.data.billing_period = billingPeriod

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting usage data")
  }
}

const changePlan = async ({ apiSource, apiKey, new_tier }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/changePlan/${new_tier}`,
      method: "patch",
      data: {}
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error changing payment plan")
  }
}


const cancelChangePlan = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/cancelChangePlan`,
      method: "patch",
      data: {}
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error cancelling payment plan change")
  }
}

const getClientPaymentCard = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/getClientPaymentCard`,
      method: "get",
      data: {}
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting payment card data")
  }
}


const setupStripeSession = async ({ apiSource, apiKey, stripe_options }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/setupStripeSession`,
      method: "post",
      data: stripe_options
    }

    const apiMode = ["local.smartix.uk","localhost"].includes(window.location.hostname) ? "test" : "live"

    return await callApi({ apiKey, includeChildClientId: false, options, apiMode })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error setting up stripe session")
  }
}


const getStripeSessionResponse = async ({ apiSource, apiKey, stripeSessionId }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/getStripeSessionResponse/${stripeSessionId}`,
      method: "get"
    }

    const apiMode = ["local.smartix.uk","localhost"].includes(window.location.hostname) ? "test" : "live"

    return await callApi({ apiKey, includeChildClientId: false, options, apiMode })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting stripe response")
  }
}

const getStripePublicKey = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/getStripePublicKey`,
      method: "get"
    }

    const apiMode = ["local.smartix.uk","localhost"].includes(window.location.hostname) ? "test" : "live"

    return await callApi({ apiKey, includeChildClientId: false, options, apiMode })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting stripe public key")
  }
}



const applyForMonthlyPlan = async ({ apiSource, apiKey, application }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/applyForMonthlyPlan`,
      method: "put",
      data: {
        application
      }
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error applying for monthly contract")
  }
}


const sendSupportRequest = async ({ apiSource, apiKey, subject, regarding, message }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/support/sendSupportRequest`,
      method: "post",
      data: {
        subject,
        regarding,
        message
      }
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error sending support request")
  }
}

const getSupportThreads = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/support/getSupportThreads`,
      method: "get"
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting support threads")
  }
}

const getSupportThreadMessages = async ({ apiSource, apiKey, thread_id }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/support/getSupportThreadMessages/${thread_id}`,
      method: "get"
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting support message")
  }
}


const closeSupportThread = async ({ apiSource, apiKey, thread_id }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/support/closeSupportThread/${thread_id}`,
      method: "patch"
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error closing support thread")
  }
}

const replyToSupportThread = async ({ apiSource, apiKey, thread_id, message, close }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/support/replyToSupportThread/${thread_id}`,
      method: "patch",
      data: {
        message,
        close
      }
    }

    return await callApi({ apiKey, includeChildClientId: false, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error replying to support thread")
  }
}



const createTestStampcard = async ({ apiSource, apiKey, templateId }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/stampcard/create/${templateId}`,
      method: "put",
      data: {}
    }

    return await callApi({ apiKey, apiMode: "test", options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error creating test stampcard ")
  }
}


const createTestLoyaltycard = async ({ apiSource, apiKey, templateId }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/loyalty/create/${templateId}`,
      method: "put",
      data: {}
    }

    return await callApi({ apiKey, apiMode: "test", options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error creating test loyalty card ")
  }
}


const createGiftcard = async ({ apiSource, apiKey, apiMode, templateId, amount, expiry }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/giftcard/create/${templateId}`,
      method: "put",
      data: { amount, expiry }
    }

    return await callApi({ apiKey, apiMode, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error creating test giftcard")
  }
}

const changeGiftcardBalance = async ({ apiSource, apiKey, templateId, passId, action, amount, note }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/giftcard/changeBalance/${templateId}/${passId}`,
      method: "patch",
      data: { amount, action, note }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error changing giftcard balance")
  }
}


const changeStampcardStamps = async ({ apiSource, apiKey, templateId, passId, action, amount, note }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/stampcard/changeStamps/${templateId}/${passId}`,
      method: "patch",
      data: { amount, action, note }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error changing stampcard stamps")
  }
}

const changeLoyaltyPoints = async ({ apiSource, apiKey, templateId, passId, action, amount, note }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/loyalty/changePoints/${templateId}/${passId}`,
      method: "patch",
      data: { amount, action, note }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error changing loyalty points ")
  }
}



const setScannerPassExpiry = async ({ apiSource, apiKey, templateId, passId, expiry }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/scanner/setExpiryDate/${templateId}/${passId}`,
      method: "patch",
      data: { expiry }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error changing pass expiry date ")
  }
}


const toggleScannerPassEnabled = async ({ apiSource, apiKey, templateId, passId, enabled, note }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/scanner/toggleEnabled/${templateId}/${passId}`,
      method: "patch",
      data: { enabled, note }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error toggle pass enabled state")
  }
}

const updatePassVariables = async ({ apiSource, apiKey, templateId, passId, variables }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/pass/updateVariables/${templateId}/${passId}`,
      method: "patch",
      data: { variables }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error updating pass variables")
  }
}


const changeEventDate = async ({ apiSource, apiKey, templateId, newEventDate, newExpiryDate, notification }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/changeEventDate/${templateId}`,
      method: "patch",
      data: { newEventDate, newExpiryDate, notification }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error changing event date")
  }
}



const getSmartixScannerPassHistory = async ({ apiSource, apiKey, passType, templateId, passId }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getSmartixScannerPassHistory/${passType}/${templateId}/${passId}`,
      method: "get"
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error passing scan history")
  }
}

const getCreditsPriceList = async ({apiSource,apiKey}) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getCreditsPriceList`,
      method: "get"
    }

    let result = await callApi({ apiKey, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting credits price list")
  }
}

const getVatRate = async ({apiSource,apiKey}) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getVatRate`,
      method: "get"
    }

    let result = await callApi({ apiKey, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting credits price list")
  }
}

const getMonthlyCreditPlans = async ({apiSource,apiKey}) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getMonthlyCreditPlans`,
      method: "get"
    }

    let result = await callApi({ apiKey, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting credit plans")
  }
}

const getCreditMatrix  = async ({apiSource,apiKey}) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getCreditMatrix`,
      method: "get"
    }

    let result = await callApi({ apiKey, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting credit matrix")
  }
}



const syncPasses = async ({ apiSource, apiKey, templateId }) => {
  try {
    let options = {
      method: 'POST',
      url: `${apiUrls[apiSource]}/syncPasses/${templateId}`
    }

    const result = await callApi({ apiKey, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error synchronising pass")
  }
}

const requestSyncCheck = async ({ apiSource, apiKey, templateId, apple = true, google = true }) => {
  try {
    let options = {
      method: 'POST',
      url: `${apiUrls[apiSource]}/syncCheck/${templateId}`,
      data: {
        apple,
        google
      }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error checking synchronisation status")
  }
}

const callApi = async ({ apiKey, apiMode, includeChildClientId = true, options }) => {
  try {
    return await callAuthenticatedApi({
      apiSource: getApiSourceFromUrl(options.url),
      apiKey,
      apiMode,
      includeChildClientId,
      options
    })
  }
  catch (err) {
    
    throw err
  }
}

const getApiSourceFromUrl = url => {
  return url?.startsWith(apiUrls.local) ? 'local' : 'remote'
}


export {
  getRealtimeConfig,
  //client
  addChildClient,
  deleteAccount,
  downloadClientCsr,
  getApiKeys,
  getCertificateManagementRows,
  getClientCertificates,
  recoverAccount,
  requestPrivateCertificate,
  saveUserPreferences,
  setClientCertificateType,
  onboarding,
  uploadOwnAppleCertificate,
  uploadOwnGoogleCredentials,

  // billing
  applyForMonthlyPlan,
  cancelChangePlan,
  changePlan,
  getClientPaymentCard,
  getPlans,
  getStripePublicKey,
  getStripeSessionResponse,
  requestSyncCheck,
  setupStripeSession,
  getUsage,
  getCreditsPriceList,
  getVatRate,
  getMonthlyCreditPlans,
  getCreditMatrix,

  // supports
  closeSupportThread,
  getSupportThreadMessages,
  getSupportThreads,
  replyToSupportThread,
  sendSupportRequest,

  // Passes
  changeEventDate,
  changeGiftcardBalance,
  changeStampcardStamps,
  changeLoyaltyPoints,
  createGiftcard,
  createTestStampcard,
  createTestLoyaltycard,
  getSmartixScannerPassHistory,
  setScannerPassExpiry,
  toggleScannerPassEnabled,
  updatePassVariables,
  syncPasses,
}
